  import React, { useEffect, useState } from "react";
  import { 
    Container, Row, Col, Card, CardBody, 
    Form, Label, Input, Button, FormFeedback, Spinner, Badge 
  } from "reactstrap";
  import { useFormik } from "formik";
  import * as Yup from "yup";
  import { get, post } from "../../helpers/api_helper";
  import withRouter from "../../components/Common/withRouter";
  import Breadcrumbs from "../../components/Common/Breadcrumb";
  import { useNavigate } from "react-router-dom";

  const EditListing = (props) => {
    const listingId = props.router.params.id;
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [existingImages, setExistingImages] = useState([]);
    const [newImages, setNewImages] = useState([]);
    const [deletedImages, setDeletedImages] = useState([]);

    // Formik Setup
    const validation = useFormik({
      enableReinitialize: true,
      initialValues: {
        title: "",
        category_id: "",
        price: "",
        currency: "RON",
        description: "",
        location: "",
        lat: "",
        lng: "",
        contact_name: "",
        phone: "",
        status: "pending_moderation",
        payment_status: "unpaid",
      },
      validationSchema: Yup.object({
        title: Yup.string().min(16).max(70).required("Titlul este obligatoriu"),
        category_id: Yup.string().required("Selectați o categorie"),
        price: Yup.number().required("Prețul este obligatoriu"),
        description: Yup.string().min(40).required("Descrierea este prea scurtă"),
        location: Yup.string().required("Localitatea este obligatorie"),
        contact_name: Yup.string().required("Nume contact obligatoriu"),
        phone: Yup.string().required("Telefon obligatoriu"),
      }),
      onSubmit: async (values) => {
        const formData = new FormData();
        
        // Append text fields
        Object.keys(values).forEach(key => formData.append(key, values[key]));
        
        // Append images to delete
        deletedImages.forEach(id => formData.append("delete_images[]", id));
        
        // Append new files
        newImages.forEach(img => formData.append("images[]", img.file));

        try {
          setLoading(true);
          // We use POST with _method=PUT for Laravel to handle Multipart FormData correctly
          formData.append("_method", "PUT");
          const response = await post(`/admin/listings/${listingId}`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          if (response.status === "success") {
            navigate("/anunturi");
          }
        } catch (error) {
          console.error("Error updating listing", error);
        } finally {
          setLoading(false);
        }
      },
    });

    useEffect(() => {
      const loadData = async () => {
        try {
          const [catRes, listRes] = await Promise.all([
            get("/admin/categories"),
            get(`/admin/listings/${listingId}`)
          ]);

          setCategories(catRes.data.flat || []);
          const l = listRes.data;
          
          validation.setValues({
            title: l.title,
            category_id: l.category_id,
            price: l.price,
            currency: l.currency,
            description: l.description,
            location: l.location,
            lat: l.lat,
            lng: l.lng,
            contact_name: l.contact_name,
            phone: l.phone,
            status: l.status,
            payment_status: l.payment_status,
          });
          setExistingImages(l.images);
        } catch (e) {
          console.error(e);
        } finally {
          setLoading(false);
        }
      };
      loadData();
    }, []);

    const handleFileChange = (e) => {
      const files = Array.from(e.target.files);
      const mapped = files.map(file => ({
        file,
        preview: URL.createObjectURL(file)
      }));
      setNewImages([...newImages, ...mapped]);
    };

    const removeExisting = (id) => {
      setDeletedImages([...deletedImages, id]);
      setExistingImages(existingImages.filter(img => img.id !== id));
    };

    if (loading) return <div className="page-content text-center"><Spinner color="primary" /></div>;

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="Admin" breadcrumbItem="Editează Anunț" />
            <Form onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); }}>
              <Row>
                <Col lg={8}>
                  <Card>
                    <CardBody>
                      <h5 className="mb-4">Detalii Anunț</h5>
                      <div className="mb-3">
                        <Label>Titlu</Label>
                        <Input name="title" type="text" onChange={validation.handleChange} value={validation.values.title} invalid={validation.touched.title && !!validation.errors.title} />
                        <FormFeedback>{validation.errors.title}</FormFeedback>
                      </div>

                      <Row>
                        <Col md={6}>
                          <div className="mb-3">
  <Label>Categorie</Label>
  <Input 
    type="select" 
    name="category_id" 
    onChange={validation.handleChange} 
    value={validation.values.category_id}
    invalid={validation.touched.category_id && !!validation.errors.category_id}
  >
    <option value="">Selectați o categorie</option>
    {categories.map((c) => (
      <option key={c.id} value={c.id}>
        {/* Adds 3 non-breaking spaces per depth level and a dash for children */}
        {"\u00A0".repeat(c.depth * 3)}
        {c.depth > 0 ? "— " : ""}
        {c.name}
      </option>
    ))}
  </Input>
  <FormFeedback>{validation.errors.category_id}</FormFeedback>
</div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Preț</Label>
                            <Input name="price" type="number" onChange={validation.handleChange} value={validation.values.price} />
                          </div>
                        </Col>
                        <Col md={2}>
                          <div className="mb-3">
                            <Label>Monedă</Label>
                            <Input type="select" name="currency" onChange={validation.handleChange} value={validation.values.currency}>
                              <option value="RON">RON</option>
                              <option value="EUR">EUR</option>
                            </Input>
                          </div>
                        </Col>
                      </Row>

                      <div className="mb-3">
                        <Label>Descriere</Label>
                        <Input name="description" type="textarea" rows="6" onChange={validation.handleChange} value={validation.values.description} />
                      </div>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardBody>
                      <h5 className="mb-4">Imagini Actuală & Noi</h5>
                      <div className="d-flex flex-wrap gap-3">
                        {/* Existing */}
                        {existingImages.map(img => (
                          <div key={img.id} className="position-relative">
                            <img src={`https://apex-front.sitemile.com/storage/${img.path}`} alt="" style={{ width: '120px', height: '120px', objectFit: 'cover' }} className="rounded border" />
                            <button type="button" className="btn btn-danger btn-sm position-absolute top-0 end-0" onClick={() => removeExisting(img.id)}>&times;</button>
                          </div>
                        ))}
                        {/* New Previews */}
                        {newImages.map((img, i) => (
                          <div key={i} className="position-relative">
                            <img src={img.preview} alt="" style={{ width: '120px', height: '120px', objectFit: 'cover' }} className="rounded border border-primary" />
                            <button type="button" className="btn btn-danger btn-sm position-absolute top-0 end-0" onClick={() => setNewImages(newImages.filter((_, idx) => idx !== i))}>&times;</button>
                          </div>
                        ))}
                        <Label className="btn btn-light d-flex align-items-center justify-content-center" style={{ width: '120px', height: '120px', border: '2px dashed #ccc' }}>
                          + Foto
                          <Input type="file" hidden multiple onChange={handleFileChange} accept="image/*" />
                        </Label>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col lg={4}>
                  <Card>
                    <CardBody>
                      <h5 className="mb-4">Status Admin</h5>
                      <div className="mb-3">
                        <Label>Status Moderare</Label>
                        <Input type="select" name="status" onChange={validation.handleChange} value={validation.values.status}>
                          <option value="active">Activ</option>
                          <option value="pending_moderation">În așteptare</option>
                          <option value="deactivated">Dezactivat</option>
                        </Input>
                      </div>
                      <div className="mb-3">
                        <Label>Status Plată</Label>
                        <Input type="select" name="payment_status" onChange={validation.handleChange} value={validation.values.payment_status}>
                          <option value="paid">Plătit</option>
                          <option value="unpaid">Neplătit</option>
                        </Input>
                      </div>
                      <hr />
                      <Button color="primary" type="submit" className="w-100 py-2">
                        Salvează Modificările
                      </Button>
                      <Button color="light" type="button" className="w-100 mt-2" onClick={() => navigate("/anunturi")}>
                        Renunță
                      </Button>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardBody>
                      <h5>Contact & Locație</h5>
                      <div className="mb-3">
                        <Label>Localitate</Label>
                        <Input name="location" value={validation.values.location} onChange={validation.handleChange} />
                      </div>
                      <div className="mb-3">
                        <Label>Persoană Contact</Label>
                        <Input name="contact_name" value={validation.values.contact_name} onChange={validation.handleChange} />
                      </div>
                      <div className="mb-3">
                        <Label>Telefon</Label>
                        <Input name="phone" value={validation.values.phone} onChange={validation.handleChange} />
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Form>
          </Container>
        </div>
      </React.Fragment>
    );
  };

  export default withRouter(EditListing);